<template>
    <page>
        <div class="content-wrapper py-3" v-if="dataLoaded">
            <div class="row">
                <div class="col-lg-8 col-xl-6">
                    <h1 class="mb-3">Clocking IDs</h1>

                    <p class="text-muted">
                        Assigns a per-client, numeric clocking ID to each employee with a
                        valid SSN. Two employees in different companies of the same client
                        share the same clocking ID when they share an SSN. Once assigned,
                        an employee's clocking ID does not change.
                    </p>

                    <div class="card">
                        <div class="card-body">
                            <div class="form-group row">
                                <label class="col-sm-3 col-form-label">Status</label>
                                <div class="col-sm-9">
                                    <label class="custom-control custom-switch">
                                        <input
                                            type="checkbox"
                                            class="custom-control-input"
                                            v-model="form.enabled"
                                        />
                                        <span class="custom-control-label">
                                            {{ form.enabled ? 'Enabled' : 'Disabled' }}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3 col-form-label">Starting ID</label>
                                <div class="col-sm-9">
                                    <input
                                        type="text"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.start }"
                                        v-model="form.start"
                                        :readonly="form.enabled"
                                    />
                                    <div class="invalid-feedback" v-if="errors.start">{{ errors.start }}</div>
                                    <small class="form-text text-muted" v-if="form.enabled">
                                        Read-only while clocking IDs are enabled.
                                    </small>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-3 col-form-label">Next ID</label>
                                <div class="col-sm-9">
                                    <input
                                        type="text"
                                        inputmode="numeric"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.next }"
                                        v-model="form.next"
                                    />
                                    <div class="invalid-feedback" v-if="errors.next">{{ errors.next }}</div>
                                    <small class="form-text text-muted">
                                        Lowering this can cause collisions with existing IDs.
                                    </small>
                                </div>
                            </div>

                        </div>
                        <div class="card-footer text-right">
                            <button
                                type="button"
                                class="btn btn-outline-primary mr-2"
                                @click="resetForm"
                                :disabled="!isDirty || saving"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="btn btn-primary"
                                @click="save"
                                :disabled="!isDirty || hasErrors || saving"
                            >
                                {{ saving ? 'Saving…' : 'Save' }}
                            </button>
                        </div>
                    </div>

                    <div class="card mt-3" v-if="config.enabled">
                        <div class="card-body">
                            <h5>Status</h5>
                            <p>
                                <strong>{{ stats.active_with_id }}</strong> of
                                <strong>{{ stats.active_with_id + stats.active_without_id }}</strong>
                                active employees have a clocking ID.
                                <span v-if="stats.skipped_no_ssn || stats.skipped_invalid_ssn">
                                    Skipped: {{ stats.skipped_no_ssn }} (no SSN),
                                    {{ stats.skipped_invalid_ssn }} (invalid SSN).
                                </span>
                            </p>

                            <button
                                type="button"
                                class="btn btn-primary"
                                @click="runBackfill"
                                :disabled="backfillStarting"
                            >
                                <i class="far fa-fw fa-sync"></i>
                                {{ backfillStarting ? 'Starting…' : 'Run backfill' }}
                            </button>
                            <a
                                class="btn btn-outline-primary ml-2"
                                :href="exportURL"
                            >
                                <i class="far fa-fw fa-file-excel"></i>
                                Export to Excel
                            </a>
                            <p class="text-muted mt-2 mb-0" v-if="backfillStartedAt">
                                Backfill started in the background at {{ backfillStartedAt.toLocaleTimeString() }}.
                                It runs asynchronously; reload the page in a few minutes to see updated stats.
                                For detailed reporting (including conflicts), run the
                                <code>backfill-client-clocking-ids</code> management command.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </page>
</template>

<script>
import Page from '@/components/Page'

const INT_RE = /^\d+$/

export default {
    name: 'SettingsClockingIds',
    components: { Page },
    props: {
        client: { type: Object, required: true },
    },
    data() {
        return {
            dataLoaded: false,
            // `config` mirrors what's saved on the server; `form` is the editable draft.
            config: { enabled: false, start: 200001, next: 200001 },
            form: { enabled: false, start: '200001', next: '200001' },
            stats: { active_with_id: 0, active_without_id: 0, skipped_no_ssn: 0, skipped_invalid_ssn: 0 },
            saving: false,
            backfillStarting: false,
            backfillStartedAt: null,
        }
    },
    computed: {
        exportURL() {
            return `${this.$api.API_BASE}/admin/clients/${this.client.id}/clocking-ids/export`
        },
        errors() {
            const errs = {}
            if (!INT_RE.test(String(this.form.start))) {
                errs.start = 'Must be a whole number.'
            }
            if (!INT_RE.test(String(this.form.next))) {
                errs.next = 'Must be a whole number.'
            }
            return errs
        },
        hasErrors() {
            return Object.keys(this.errors).length > 0
        },
        isDirty() {
            return (
                this.form.enabled !== this.config.enabled
                || String(this.form.start) !== String(this.config.start)
                || String(this.form.next) !== String(this.config.next)
            )
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Clocking IDs')
        this.fetchConfig()
    },
    methods: {
        fetchConfig() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/admin/clients/${this.client.id}/clocking-ids`).then((data) => {
                this.config.enabled = data.enabled
                this.config.start = data.start
                this.config.next = data.next
                this.stats = data.stats
                this.resetForm()
                this.dataLoaded = true
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__ ? errors.__all__[0] : 'An error occurred.')
            })
        },
        resetForm() {
            this.form.enabled = this.config.enabled
            this.form.start = String(this.config.start)
            this.form.next = String(this.config.next)
        },
        save() {
            if (this.hasErrors || !this.isDirty) return

            const newStart = parseInt(this.form.start, 10)
            const newNext = parseInt(this.form.next, 10)

            if (newNext < this.config.next) {
                const ok = window.confirm(
                    'Lowering Next ID may cause collisions if it overlaps existing assigned IDs. Continue?'
                )
                if (!ok) return
            }

            // Only send fields that actually changed; the backend treats any
            // present field as a mutation request (e.g. rejecting `start`
            // changes while enabled).
            const payload = {}
            if (this.form.enabled !== this.config.enabled) payload.enabled = this.form.enabled
            if (newStart !== this.config.start) payload.start = newStart
            if (newNext !== this.config.next) payload.next = newNext

            this.saving = true
            this.$store.dispatch('START_LOADING')
            this.$api.patch(`/admin/clients/${this.client.id}/clocking-ids`, payload).then(() => {
                this.saving = false
                this.$store.dispatch('STOP_LOADING')
                this.fetchConfig()
            }).catch((errors) => {
                this.saving = false
                this.$store.dispatch('STOP_LOADING')
                const msg = errors.__all__
                    ? errors.__all__[0]
                    : (errors.start ? errors.start[0] : (errors.next ? errors.next[0] : 'Failed to save.'))
                this.$bus.showError(msg)
            })
        },
        runBackfill() {
            const ok = window.confirm('Run backfill now? This will run in the background.')
            if (!ok) return
            this.backfillStarting = true
            this.$store.dispatch('START_LOADING')
            this.$api.post(`/admin/clients/${this.client.id}/clocking-ids/backfill`, {}).then(() => {
                this.backfillStarting = false
                this.backfillStartedAt = new Date()
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.backfillStarting = false
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__ ? errors.__all__[0] : 'Failed to start backfill.')
            })
        },
    },
}
</script>
